/**
 * advanceCommit.ts — what an advance's save is allowed to be, and the order it happens in.
 *
 * REFUSE → WRITE THE LOAN → REWRITE THE ONE RATE ROW, and every arrow is load-bearing.
 *
 * REFUSE FIRST, WHICH IS `moneyCommit`'s RULE APPLIED AGAIN. These are unwrapped round trips with
 * no transaction around them, so a refusal that arrives after `loans` has been written leaves an
 * advance the admin did not mean to create — and an advance that exists is money in
 * `total_value` and a row in the Unassigned queue's "assigned" column.
 *
 * THE SCHEDULE IS REWRITTEN, NEVER CLEARED AND RE-INSERTED. The side sheet this replaces deleted
 * every `loan_instalments` row unconditionally and re-inserted only where the weekly figure was
 * more than zero, with no compensating restore — so a failure between the two statements, or an
 * admin who cleared one field, left an advance that silently bills nothing [F7]. The rewrite here
 * keeps ONE rate row and writes it BEFORE it deletes anything: one INSERT of the day the form
 * shows, adopted into the row that already holds that day when the unique index refuses it, then a
 * DELETE of every other row of the loan. No failure of a single save leaves the advance with no
 * schedule at all, and no row survives because this client had not read it. The one window two
 * unwrapped round trips cannot shut — two saves to DIFFERENT days interleaving their halves — is
 * named at `scheduleStatements` with the server-side statement that would shut it.
 *
 * THE WRITE IS AN INSERT EVEN WHERE THE DAY LOOKS UNCHANGED, and that is not a round trip wasted.
 * An UPDATE is only a write where the row it names exists: PostgREST answers an UPDATE matching
 * nothing with `error: null`, so a client whose `loan_instalments` read is behind the server — no
 * `staleTime` on the board's subjects, `refetchOnWindowFocus: false` app-wide — would write nothing
 * and then delete everything else, which is the side sheet's silent-advance failure by a newer
 * route. An INSERT cannot match nothing; the `23505` adopt below IS the in-place update.
 *
 * THE `23505` ADOPT IS THE SIDE SHEET'S, AND IT IS NOT DEFENSIVENESS. `UNIQUE(loan_id, starts_at)`
 * refuses a second row on one date, and `weekly_rate` SUMS every instalment sharing a loan's latest
 * start date — so two overlapping saves that both inserted would bill this advance twice. The
 * second writer adopts the row that won instead.
 *
 * THE PLANNER IS SEPARATE FROM THE CALLS, AND THAT IS THE WHOLE TESTABILITY OF THIS FILE. The
 * ORDER of the statements is the safety property; `scheduleStatements` returns it as data, so a
 * test pins it without a network.
 */

import type { SupabaseClient } from '@supabase/supabase-js';

import { agreementBoardSubjectsKey } from '@/components/agreements/boardSubjects';
import type { Database } from '@/integrations/supabase/types';

import type { AdvanceArea } from './advanceSteps';
import {
    firstStartsAt,
    latestInstalment,
    type AdvanceInstalmentFact,
    type AdvanceRowId,
} from './advanceState';

/** The client, structurally — injected so this module never imports the browser singleton. */
export type AdvanceClient = SupabaseClient<Database>;

export type AdvanceMode = 'create' | 'edit';

/**
 * The figures as the dossier holds them, already parsed.
 *
 * `null` IS "NOT PROVIDED" AND NEVER ZERO, for `parseAmount`'s reason: `Number('')` is 0, and a
 * weekly repayment of nought written as a figure is an advance that bills nothing while claiming a
 * schedule.
 */
export interface AdvanceDraft {
    /** `loans.transaction_id` — the disbursement. NOT NULL in SQL. */
    transactionId: number | null;
    /** The disbursement's amount, as the selector resolved it. Read by the proof-read, never written. */
    amount: number | null;
    /** `loans.interest_rate`, per cent. Absent writes 0, which is the sheet's own spelling. */
    interestRate: number | null;
    /** `loans.description` — the Schedule's `{{loan.purpose}}`. */
    purpose: string | null;
    /** `loan_instalments.amount` — the weekly repayment. */
    weeklyAmount: number | null;
    /** `loan_instalments.starts_at` — a CALENDAR day, `'YYYY-MM-DD'`, never an instant. */
    firstStartsAt: string | null;
}

/** The create's draft, with the contract it is opened on. */
export interface AdvanceCreate extends AdvanceDraft {
    contractId: number;
}

/** The advance as it stands on the server — what an edit is judged against. */
export interface AdvanceExisting {
    /** `loan_instalments`, as read. */
    instalments: AdvanceInstalmentFact[];
    /**
     * `loans.description` as it stands on the server — the Schedule's `{{loan.purpose}}`.
     *
     * IT IS HERE SO AN EDIT CANNOT CLEAR IT. A purpose on file may be corrected and not emptied, for
     * the schedule's own reason one branch below; a loan that never had one stays saveable, because
     * refusing every other correction on it would strand it.
     */
    purpose: string | null;
    /** [D-B54] — below the recovery watermark and never scheduled: the repayment fields are a READ. */
    legacyNoSchedule: boolean;
}

export type AdvanceRefusalField = 'transaction' | 'purpose' | 'weekly' | 'first' | 'schedule';

export interface AdvanceRefusal {
    field: AdvanceRefusalField;
    /** The area holding the field, so the commit bar's hint can say where to go. */
    area: AdvanceArea;
    /** The rail row this lands on, where it has one. */
    rowId: AdvanceRowId | null;
    message: string;
}

/**
 * THE SENTENCES THE COMMIT BAR SPEAKS, and they carry no decision id: an admin reading a refusal
 * is being told what to do, not which ruling it came from. The ids stay in the docblocks.
 */
const REFUSALS = {
    transaction: 'Select the disbursement this advance paid out.',
    purpose: 'Say what this advance is for — the Schedule prints it, and cannot render without it.',
    /** [D-B54] — the weekly repayment is the rule, so its absence is a refusal and not a default. */
    weekly: 'Enter the weekly repayment. Every advance is repaid weekly.',
    first: 'Choose the day the first repayment falls due.',
    purposeCleared:
        'The Schedule prints what this advance is for and cannot render without it — it can be ' +
        'corrected, not cleared.',
    positive: 'The weekly repayment has to be more than zero.',
    half: 'A repayment schedule needs both the weekly amount and the day it starts.',
    cleared: 'An existing repayment schedule can be corrected, not cleared.',
    /** [D-B54] — the 47 pre-watermark advances the rule deliberately does not reach. */
    legacy:
        'This advance predates the weekly-schedule rule and is settled at completion. Its ' +
        'repayment cannot be set here.',
} as const;

function filled(value: string | null | undefined): boolean {
    return !!value && value.trim().length > 0;
}

/**
 * Every reason this draft cannot be saved, in reading order.
 *
 * ALL OF THEM, NOT THE FIRST — the commit bar names one and counts the rest, and an admin who
 * fixes the named one should not discover a second refusal they could have fixed in the same pass.
 *
 * THE FOUR ON CREATE ARE [D-B54]'s: an advance names its disbursement, says what it is for, and comes
 * back weekly from a stated day. There is no "settled through the regular instalment" any more —
 * the switch that offered it is what this flow replaces.
 *
 * ON EDIT THE SCHEDULE MAY BE CORRECTED AND NOT CLEARED, and a half-written one is refused rather
 * than silently skipped: the sheet's edit path wrote a date with no amount as "no schedule at all",
 * which is an advance that stops billing without anybody deciding it should.
 *
 * THE PURPOSE IS THE SAME SHAPE, AND FOR A LOUDER REASON. `{{loan.purpose}}` resolves through
 * `NULLIF(btrim(...), '')` and is render-blocking [F5]; the render is LIVE until the document is
 * frozen at the first signature, and nothing blocks a send or a signature on merge completeness
 * [F0]. So an edit that emptied the box on an advance whose Schedule is already out would hand the
 * driver a document printing the merge key literally. A purpose on file is corrected, not cleared.
 */
export function validateDraft(
    draft: AdvanceDraft,
    mode: AdvanceMode,
    existing: AdvanceExisting | null,
): AdvanceRefusal[] {
    const refusals: AdvanceRefusal[] = [];
    const push = (
        field: AdvanceRefusalField,
        area: AdvanceArea,
        rowId: AdvanceRowId | null,
        message: string,
    ) => refusals.push({ field, area, rowId, message });

    // `loans.transaction_id` is NOT NULL: the principal IS the disbursement's amount, and an
    // advance without one states no money at all.
    if (draft.transactionId === null || draft.transactionId === undefined) {
        push('transaction', 'disbursement', 'adv-transaction', REFUSALS.transaction);
    }

    if (!filled(draft.purpose) && (mode === 'create' || filled(existing?.purpose))) {
        push(
            'purpose',
            'disbursement',
            'adv-purpose',
            mode === 'create' ? REFUSALS.purpose : REFUSALS.purposeCleared,
        );
    }

    const hasWeekly = draft.weeklyAmount !== null && draft.weeklyAmount !== undefined;
    const hasDate = filled(draft.firstStartsAt);

    if (existing?.legacyNoSchedule) {
        // The door D-B54 shut. Nothing else about the advance is affected — the purpose and the rate
        // stay editable on a legacy row, because neither moves any money.
        if (hasWeekly || hasDate) {
            push('schedule', 'repayment', 'adv-weekly', REFUSALS.legacy);
        }
        return refusals;
    }

    if (hasWeekly && (draft.weeklyAmount as number) <= 0) {
        push('weekly', 'repayment', 'adv-weekly', REFUSALS.positive);
    }

    if (hasWeekly !== hasDate) {
        push('schedule', 'repayment', hasWeekly ? 'adv-first' : 'adv-weekly', REFUSALS.half);
        return refusals;
    }

    if (!hasWeekly && !hasDate) {
        if (mode === 'create') {
            push('weekly', 'repayment', 'adv-weekly', REFUSALS.weekly);
            push('first', 'repayment', 'adv-first', REFUSALS.first);
        } else if ((existing?.instalments.length ?? 0) > 0) {
            push('schedule', 'repayment', 'adv-weekly', REFUSALS.cleared);
        }
    }

    return refusals;
}

// ─── The rewrite, as data ────────────────────────────────────────────────────

export type ScheduleStatement =
    | { op: 'insert'; startsAt: string; amount: number }
    | { op: 'delete-others'; keep: string };

/**
 * The statements that leave this advance with exactly ONE rate row, in the order they run.
 *
 * WRITE BEFORE DELETE, AND THE WRITE IS TOTAL. The day the form shows is INSERTed first — adopted
 * into whatever row already holds that day — and everything else deleted after, so a failure
 * anywhere in the sequence leaves the advance with a schedule rather than without one. Neither half
 * is conditioned on what this client read, because a conditional write beside an unconditional
 * delete is the worst of both: an UPDATE aimed at a day the server does not hold matches nothing,
 * reports no error, and the DELETE behind it carries off the real rate row. The reverse order, and
 * the conditional write, are both the side sheet's failure mode — an advance that quietly bills
 * nothing.
 *
 * THE DELETE IS SCOPED TO THE LOAN AND NOT TO THE DATES THIS CLIENT HAPPENED TO READ. The sheet
 * deleted every row of the loan unconditionally, so it could not leave behind a row it had not seen;
 * a planner that names the dates it read can. Two overlapping saves that move the day to DIFFERENT
 * dates would each insert their own row and each delete only the one they both started from,
 * leaving two rows the sim's `loan_instalment_row_duplicated` invariant counts — and `weekly_rate`
 * billing whichever holds `MAX(starts_at)` rather than whichever was saved last. A cache one refetch
 * behind the server opens the same hole. So the supersede is "everything on this loan except the day
 * just written", which is true whatever this client read.
 *
 * WHAT NO CLIENT CAN CLOSE is two saves moving the day to DIFFERENT dates whose halves interleave:
 * insert(D2), insert(D3), delete-all-but-D2, delete-all-but-D3 ends with no row at all, and no
 * ordering of two unwrapped round trips prevents it. Closing it takes ONE server-side statement —
 * an upsert that supersedes in the same transaction — and that is named as the follow-up.
 *
 * NOTHING MOVED, NOTHING IS WRITTEN. Every save carries the WHOLE draft — a purpose correction
 * committed from the Disbursement bar arrives with the repayment pair the form read back — and the
 * form reads that pair as the server PRINTS it: the LATEST row's amount beside the EARLIEST row's
 * date. That pairing is right for a Schedule and wrong as a write target, so an unchanged pair plans
 * no rewrite at all rather than re-stamping the current weekly onto the first Monday and dropping
 * the rate steps in between.
 *
 * NO DRAFT SCHEDULE, NO STATEMENTS. Clearing is refused upstream (`validateDraft`), so an empty
 * pair here is an advance whose repayment was never being edited — a purpose correction on a legacy
 * row, say — and the rows it already has are left exactly as they are.
 */
export function scheduleStatements(
    draft: AdvanceDraft,
    existing: AdvanceExisting | null,
): ScheduleStatement[] {
    const amount = draft.weeklyAmount;
    const startsAt = draft.firstStartsAt;
    if (amount === null || amount === undefined || !filled(startsAt)) return [];

    const day = (startsAt as string).trim();
    if (!existing) return [{ op: 'insert', startsAt: day, amount }];

    const rows = existing.instalments;
    if (amount === latestInstalment(rows)?.amount && day === firstStartsAt(rows)) return [];

    return [
        { op: 'insert', startsAt: day, amount },
        { op: 'delete-others', keep: day },
    ];
}

// ─── The writes ──────────────────────────────────────────────────────────────

/** `23505` — `UNIQUE(loan_id, starts_at)` refused a second row on one date. */
const DUPLICATE = '23505';

async function applyStatement(
    client: AdvanceClient,
    loanId: number,
    statement: ScheduleStatement,
): Promise<void> {
    if (statement.op === 'delete-others') {
        // SCOPED TO THE LOAN, EXCEPTING THE DAY JUST WRITTEN — never to the dates this client read.
        // `starts_at` is NOT NULL, so `<>` leaves nothing behind.
        const { error } = await client
            .from('loan_instalments')
            .delete()
            .eq('loan_id', loanId)
            .neq('starts_at', statement.keep);
        if (error) throw error;
        return;
    }

    const { error } = await client
        .from('loan_instalments')
        .insert({ loan_id: loanId, amount: statement.amount, starts_at: statement.startsAt });
    if (!error) return;
    if (error.code !== DUPLICATE) throw error;

    // ADOPT, DO NOT DUPLICATE — the side sheet's fallback, for the reason stated at the top of this
    // file: `weekly_rate` sums every row sharing a loan's latest start date. This is ALSO the
    // in-place correction of an amount on a day that has not moved: the refusal is the server
    // telling this client the row is there, which is the one answer an UPDATE would not have given.
    const { error: adoptError } = await client
        .from('loan_instalments')
        .update({ amount: statement.amount })
        .eq('loan_id', loanId)
        .eq('starts_at', statement.startsAt);
    if (adoptError) throw adoptError;
}

/**
 * Create the advance: the `loans` row, then its one instalment row.
 *
 * AND UNDO THE LOAN IF THE INSTALMENT FAILS. These are two unwrapped round trips, and the advance
 * that survives a failed instalment is not a harmless stub: `weekly_rate` bills a loan through its
 * instalments, so a loan saved without the plan the admin typed is an advance that quietly charges
 * nothing while holding the disbursement transaction. The side sheet rolled its own half-write back
 * the same way, and the values written here are that sheet's, field for field.
 */
export async function createAdvance(
    client: AdvanceClient,
    draft: AdvanceCreate,
): Promise<{ id: number }> {
    const refusals = validateDraft(draft, 'create', null);
    if (refusals.length > 0) throw new Error(refusals[0].message);

    const { data: loan, error: loanError } = await client
        .from('loans')
        .insert({
            contract_id: draft.contractId,
            interest_rate: draft.interestRate ?? 0,
            description: draft.purpose || null,
            transaction_id: draft.transactionId as number,
        })
        .select('id')
        .single();

    if (loanError) throw loanError;

    const statements = scheduleStatements(draft, null);
    for (const statement of statements) {
        try {
            await applyStatement(client, loan.id, statement);
        } catch (error) {
            await client.from('loans').delete().eq('id', loan.id);
            throw error;
        }
    }

    return { id: loan.id };
}

/**
 * Correct an advance: the `loans` row, then the one-row schedule rewrite.
 *
 * THE LOAN GOES FIRST BECAUSE THE SCHEDULE IS THE RISKY HALF. A refused UPDATE on `loans` leaves
 * everything as it was; the instalment rewrite is where a partial write can land, and putting it
 * last means the failure an admin sees is the one they can retry without the figures above it
 * having moved underneath them.
 */
export async function updateAdvance(
    client: AdvanceClient,
    loanId: number,
    draft: AdvanceDraft,
    existing: AdvanceExisting | null,
): Promise<{ id: number }> {
    const refusals = validateDraft(draft, 'edit', existing);
    if (refusals.length > 0) throw new Error(refusals[0].message);

    const { error: loanError } = await client
        .from('loans')
        .update({
            interest_rate: draft.interestRate ?? 0,
            description: draft.purpose || null,
            transaction_id: draft.transactionId as number,
        })
        .eq('id', loanId);
    if (loanError) throw loanError;

    for (const statement of scheduleStatements(draft, existing)) {
        await applyStatement(client, loanId, statement);
    }

    return { id: loanId };
}

/**
 * What a save moves, and therefore what has to be refetched.
 *
 * THE SHEET'S THREE, PLUS THE BOARD'S SUBJECTS. Creating or renaming an advance changes what the
 * `'all'` board may draw — the LOAN slot itself, and the purpose the Advances card prints — so the
 * fourth key is what stops a new advance appearing with no agreement row until a hard refresh.
 *
 * `String(contractId)` IS NOT A TYPO. The insights query is keyed `['contract-insights', contractId]`
 * where `contractId` is the ROUTE PARAM — a string — so a number here would match nothing and
 * refetch nothing (`ContractInsights.tsx`, and the six writers that already spell it `String(...)`).
 */
export function advanceInvalidationKeys(contractId: number): readonly unknown[][] {
    return [
        ['contract-insights', String(contractId)],
        ['loans'],
        ['unassigned-transactions'],
        [...agreementBoardSubjectsKey(contractId)],
    ];
}
